import { motion } from "motion/react";
import { CheckCircle2, Shield, Zap, Sparkles, ArrowRight } from "lucide-react";

export function OfferSection() {
  const offerIncludes = [
    { feature: "Dashboard Completo", description: "Visão 360° consolidada com métricas de receita" },
    { feature: "Events & Session Replay Logic", description: "Rastreamento completo de comportamento" },
    { feature: "User Profile V32 (Identity Resolution)", description: "Sistema de identificação PhD Level" },
    { feature: "Form Builder & Cart Recovery", description: "Capture leads e recupere vendas perdidas" },
    { feature: "Bot Detection Avançado", description: "Proteção multi-camadas contra tráfego falso" },
  ];

  const benefits = [
    { icon: Zap, label: "Instalação Simples", description: "Plugin nativo WordPress" },
    { icon: Shield, label: "Suporte PhD", description: "Documentação técnica detalhada para devs" },
    { icon: Sparkles, label: "Atualizações Constantes", description: "Sempre à frente das mudanças do WordPress e WooCommerce" },
  ];

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Radial Gradient Background */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(0,245,255,0.15), rgba(16,185,129,0.1), transparent 70%)",
          }}
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,245,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,245,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Sparkles className="w-8 h-8 text-[#FF4D00]" />
              <span className="font-['Roboto_Mono'] text-[#FF4D00] text-sm tracking-[0.3em] uppercase">
                Oferta Especial de Lançamento
              </span>
            </div>
            <h2 className="font-['Sora'] text-4xl md:text-6xl font-bold text-white mb-6">
              Assuma o Controle
              <br />
              <span className="text-[#00F5FF] drop-shadow-[0_0_40px_rgba(0,245,255,0.6)]">
                Hoje.
              </span>
            </h2>
            <p className="font-['Inter'] text-2xl text-gray-300 max-w-2xl mx-auto">
              O WP Total Tracker não é uma despesa.
              <br />
              É um{" "}
              <span className="text-[#10B981] font-bold text-3xl drop-shadow-[0_0_20px_rgba(16,185,129,0.5)]">
                ativo de inteligência de mercado
              </span>
              .
            </p>
          </motion.div>

          {/* Main Offer Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative bg-gradient-to-br from-[#0B0E14] to-[#0B0E14]/80 backdrop-blur-2xl border-2 border-[#00F5FF] rounded-3xl overflow-hidden"
            style={{
              boxShadow: "0 0 80px rgba(0,245,255,0.3), inset 0 0 100px rgba(0,245,255,0.08)",
            }}
          >
            {/* Offer Badge */}
            <div className="absolute top-8 right-8">
              <motion.div
                className="bg-[#FF4D00] px-6 py-3 rounded-xl"
                animate={{
                  boxShadow: [
                    "0 0 20px rgba(255,77,0,0.4)",
                    "0 0 40px rgba(255,77,0,0.8)",
                    "0 0 20px rgba(255,77,0,0.4)",
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <p className="font-['Roboto_Mono'] text-sm font-bold text-white">
                  🔥 LANÇAMENTO
                </p>
              </motion.div>
            </div>

            <div className="p-12">
              {/* Offer Title */}
              <div className="text-center mb-10">
                <h3 className="font-['Sora'] text-3xl font-bold text-white mb-4">
                  Licença Anual para Site Ilimitado
                </h3>
                <p className="font-['Roboto_Mono'] text-sm text-gray-400 uppercase tracking-wider">
                  Acesso Total a Todas as Funcionalidades
                </p>
              </div>

              {/* Included Features */}
              <div className="grid md:grid-cols-2 gap-6 mb-10">
                {offerIncludes.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-4 bg-[#0B0E14]/60 border border-[#00F5FF]/20 rounded-xl p-5 hover:border-[#00F5FF]/50 transition-all"
                  >
                    <CheckCircle2 className="w-6 h-6 text-[#10B981] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-['Sora'] text-lg font-bold text-white mb-1">
                        {item.feature}
                      </h4>
                      <p className="font-['Inter'] text-sm text-gray-400">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Pricing */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="bg-gradient-to-r from-[#00F5FF]/10 to-[#10B981]/10 border-2 border-[#00F5FF]/40 rounded-2xl p-8 mb-10 text-center"
              >
                <p className="font-['Roboto_Mono'] text-sm text-gray-400 mb-2 uppercase tracking-wider">
                  Investimento Anual
                </p>
                <div className="flex items-center justify-center gap-4 mb-4">
                  <span className="font-['Sora'] text-6xl font-bold text-[#00F5FF] drop-shadow-[0_0_30px_rgba(0,245,255,0.6)]">
                    R$ 997
                  </span>
                  <div className="text-left">
                    <p className="font-['Roboto_Mono'] text-xs text-gray-500">ou 12x de</p>
                    <p className="font-['Sora'] text-2xl font-bold text-white">R$ 97</p>
                  </div>
                </div>
                <p className="font-['Inter'] text-sm text-gray-400">
                  Equivalente a <span className="text-[#10B981] font-semibold">2 meses de Hotjar ou Crazy Egg</span>
                </p>
              </motion.div>

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7 }}
                className="text-center"
              >
                <button className="group relative px-12 py-6 bg-[#FF4D00] text-white font-['Sora'] text-xl font-bold rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-[0_0_60px_rgba(255,77,0,0.8)]">
                  <span className="relative z-10 flex items-center gap-3">
                    Quero Meus Dados Organizados Agora
                    <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
                  </span>
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-[#FF4D00] via-[#FF6B00] to-[#FF4D00]"
                    animate={{ x: ["-100%", "100%"] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  />
                </button>
              </motion.div>
            </div>
          </motion.div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-3 gap-8 mt-12 mb-12">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="bg-[#0B0E14]/60 backdrop-blur-xl border border-[#00F5FF]/20 rounded-2xl p-6 text-center hover:border-[#00F5FF]/50 transition-all"
              >
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{
                    backgroundColor: "#00F5FF20",
                    boxShadow: "0 0 20px rgba(0,245,255,0.3)",
                  }}
                >
                  <benefit.icon className="w-8 h-8 text-[#00F5FF]" />
                </div>
                <h4 className="font-['Sora'] text-xl font-bold text-white mb-2">
                  {benefit.label}
                </h4>
                <p className="font-['Inter'] text-sm text-gray-400">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Guarantee Section */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-r from-[#10B981]/20 to-[#00F5FF]/20 border-2 border-[#10B981] rounded-3xl p-10 text-center"
            style={{
              boxShadow: "0 0 60px rgba(16,185,129,0.25), inset 0 0 80px rgba(16,185,129,0.08)",
            }}
          >
            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
              {/* Guarantee Badge */}
              <motion.div
                className="relative"
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div
                  className="w-32 h-32 rounded-full border-4 border-[#10B981] flex items-center justify-center"
                  style={{
                    background: "radial-gradient(circle, rgba(16,185,129,0.3), rgba(16,185,129,0.1))",
                    boxShadow: "0 0 40px rgba(16,185,129,0.5)",
                  }}
                >
                  <Shield className="w-16 h-16 text-[#10B981]" />
                </div>
                <div className="absolute -bottom-2 -right-2 bg-[#10B981] text-white font-['Roboto_Mono'] text-xs px-3 py-1 rounded-full font-bold">
                  30 DIAS
                </div>
              </motion.div>

              {/* Guarantee Text */}
              <div className="text-left">
                <h3 className="font-['Sora'] text-3xl font-bold text-white mb-3">
                  Garantia Incondicional de 30 Dias
                </h3>
                <p className="font-['Inter'] text-lg text-gray-300 mb-2">
                  Se seus dados não ficarem mais organizados, devolvemos seu investimento.
                </p>
                <p className="font-['Roboto_Mono'] text-sm text-[#10B981]">
                  Sem perguntas. Sem burocracia. Risco zero para você.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-center mt-16"
          >
            <p className="font-['Sora'] text-2xl text-white mb-6">
              Não perca mais vendas no escuro.
            </p>
            <button className="px-10 py-4 bg-[#00F5FF] text-[#0B0E14] font-['Sora'] text-lg font-bold rounded-xl hover:scale-105 transition-all hover:shadow-[0_0_40px_rgba(0,245,255,0.6)]">
              Ver Demonstração ao Vivo (Demo)
            </button>
          </motion.div>

          {/* Social Proof Placeholder */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="mt-12 text-center"
          >
            <div className="flex items-center justify-center gap-2 text-gray-400">
              <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
              <span className="font-['Roboto_Mono'] text-sm">
                Usado por 500+ lojas WooCommerce de alto volume
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
